import * as React from "react";

import {
  IContextualMenuItem,
  IContextualMenuRenderItem,
  IMenuItemStyles,
} from "@fluentui/react/lib/ContextualMenu";
import { FocusZoneDirection } from "@fluentui/react/lib/FocusZone";

import { dataUniqueId } from "@1js/acui-common";
import {
  AppIcons,
  getSubmenuIconProps,
  iconNameToIconProps,
} from "@1js/acui-icons";
import { menuItemInMenuOnExecute } from "@1js/acui-keytip";
import {
  memoizationPropReplacer,
  memoizeLastResultPerId,
} from "@1js/acui-memoization";
import {
  ControlInMenuRendererFunction,
  convertMenuDefinitionToFabricSubMenuProps,
  deriveMenuKeytip,
  MenuDefinition,
} from "@1js/acui-menu";

import { subMenuItemExecute } from "../../utilities";
import { ControlInMenuStyleFnOrObject } from "../AppBaseButton";
import { AppFlyoutAnchorProps } from "./AppFlyoutAnchor.Props";
import { getClassNamesForFlyoutAnchorMenuButton } from "./AppFlyoutAnchorInMenu.classNames";

const getAppFlyoutAnchorSubMenuProps = (
  _id: string,
  controlProps: AppFlyoutAnchorProps,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  onSubMenuPositioned?: (
    menuDefinition: MenuDefinition | undefined,
    flyoutMenuId?: string
  ) => void,
  subMenuItemStyles?: Partial<IMenuItemStyles>,
  arePerfImprovementsEnabled?: boolean,
  onMenuItemExecuted?: (id: string, parentId: string) => void,
  shouldMergeStylesIntoMenuItems?: boolean
) => {
  return {
    ...convertMenuDefinitionToFabricSubMenuProps(
      {
        menuDefinition: controlProps.menuDefinition,
        controlInMenuRendererFunction,
        location: controlProps.location,
        onSubMenuPositioned,
        subComponentStyles: subMenuItemStyles,
        customMenuClassNames: controlProps.menuClassNames,
        arePerfImprovementsEnabled,
        onMenuItemExecuted: subMenuItemExecute(
          controlProps.id,
          onMenuItemExecuted
        ),
        shouldMergeStylesIntoMenuItems,
      },
      controlProps.id
    ),
    onMenuOpened: (): void =>
      controlProps.onExecute &&
      controlProps.onExecute({
        id: controlProps.id,
        location: controlProps.location,
      }),
    onMenuDismissed: (): void =>
      controlProps.onAfterMenuDismiss &&
      controlProps.onAfterMenuDismiss(controlProps.id),
  };
};

const getAppFlyoutAnchorSubMenuPropsWithMemoization = memoizeLastResultPerId(
  getAppFlyoutAnchorSubMenuProps,
  memoizationPropReplacer
);

export const getAppFlyoutAnchorMenuItem = (
  _idAndLocation: string,
  controlProps: AppFlyoutAnchorProps,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  appIcons: AppIcons,
  onSubMenuPositioned?: (
    menuDefinition: MenuDefinition | undefined,
    flyoutMenuId?: string
  ) => void,
  rootMenuItemStyles?: ControlInMenuStyleFnOrObject,
  subMenuItemStyles?: Partial<IMenuItemStyles>,
  arePerfImprovementsEnabled?: boolean,
  onMenuItemExecuted?: (id: string, parentId: string) => void,
  shouldMergeStylesIntoMenuItems?: boolean
): IContextualMenuItem => {
  const contextualItem = React.createRef<IContextualMenuRenderItem>();
  const getSubMenuProps = arePerfImprovementsEnabled
    ? getAppFlyoutAnchorSubMenuPropsWithMemoization
    : getAppFlyoutAnchorSubMenuProps;
  return {
    key: controlProps.id,
    name: controlProps.labelInMenu || controlProps.label,
    iconProps: iconNameToIconProps(
      controlProps.icon,
      controlProps.iconColor,
      controlProps.hideIcon
    ),
    subMenuProps: getSubMenuProps(
      _idAndLocation,
      controlProps,
      controlInMenuRendererFunction,
      onSubMenuPositioned,
      subMenuItemStyles,
      arePerfImprovementsEnabled,
      onMenuItemExecuted,
      shouldMergeStylesIntoMenuItems
    ),
    submenuIconProps: getSubmenuIconProps(appIcons),
    arrowDirection: FocusZoneDirection.bidirectional,
    disabled: controlProps.disabled,
    getItemClassNames: getClassNamesForFlyoutAnchorMenuButton(
      rootMenuItemStyles,
      shouldMergeStylesIntoMenuItems ? controlProps.styles : undefined
    ),
    keytipProps:
      controlProps.keytipProps &&
      deriveMenuKeytip(
        controlProps.keytipProps,
        (executeTarget: HTMLElement) => {
          menuItemInMenuOnExecute(executeTarget, contextualItem);
        }
      ),
    componentRef: contextualItem,
    lang: controlProps.lang,
    checked: controlProps.toggled,
    role: controlProps.roleInMenu || "menuitem",
    "data-unique-id": dataUniqueId(controlProps),
    "aria-keyshortcuts": controlProps.keyShortcuts,
  };
};

export const getAppFlyoutAnchorMenuItemWithMemoization = memoizeLastResultPerId(
  getAppFlyoutAnchorMenuItem,
  memoizationPropReplacer
);
